import { defineComponent as _defineComponent } from 'vue'
import * as validation from 'JS/components/blain-form/validation';
import ApplyGiftCardModal from 'JS/components/payment/ApplyGiftCardModal.vue';
import BForm from 'JS/components/blain-form/BForm.vue';
import BRadioGroup from 'JS/components/blain-form/BRadioGroup.vue';
import BTextField from 'JS/components/blain-form/BTextField.vue';
import bugsnagClient from 'JS/global/bugsnag';
import ConfirmRemovalModal from 'JS/components/payment/ConfirmRemovalModal.vue';
import CreditCardIcons from 'JS/components/CreditCardIcons.vue';
import CreditCardNumberField from 'JS/components/blain-form/CreditCardNumberField.vue';
import FieldNote from "Src/js/components/FieldNote.vue";
import Icon from 'JS/components/Icon.vue';
import LinkButton from 'JS/components/LinkButton.vue';
import PaymentStepOption from './PaymentStepOption.vue';
import SelectedPayment from 'JS/components/payment/SelectedPayment.vue';
import StepControls from '../StepControls.vue';
import TransitionExpand from 'JS/components/transitions/TransitionExpand.vue';
import ZipAddressInput from 'JS/components/ZipAddressInput.vue';
import { AutomotiveSchedulerStep, IWorkOrderPaymentInfoResource, PrepaymentOption, WorkOrderErrorType, WorkOrderPaymentFlow, WorkOrderTotalType } from 'JS/automotive/auto-service-scheduler/types/session';
import { computed, nextTick, onBeforeMount, onMounted, reactive, ref, watch } from 'vue';
import { CreditCardDetails } from '../../../../../types/CyberSource';
import { cyberSourceErrors } from 'JS/constants/cybersource.constants';
import { DEFAULT_GLOBAL_ERROR_MESSAGE } from 'JS/constants/errors';
import { focusNextError } from 'JS/components/blain-form/errors';
import { FormatOptions } from 'JS/components/blain-form/formatting';
import { getIntialFocusElement } from 'JS/utilities/dom';
import { ICheckoutPayment } from 'JS/types/Checkout';
import { loadFlexMicroformsClientLibrary, MicroformCardNumberField, tryGetMicroformFromBForm } from 'JS/checkout/services/CyberSourceMicroformsHelper';
import { useApplyGiftCardModal } from 'JS/automotive/auto-service-scheduler/composables/apply-gift-card';
import { useGlobalLoader } from 'JS/automotive/auto-service-scheduler/state/global-loader';
import { useOrderStore } from '../../../state/order';
import { useServices } from 'JS/automotive/auto-service-scheduler/services';
import { WORK_ORDER_DISCLAIMER_ID } from 'JS/automotive/auto-service-scheduler/constants';


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentStep',
  emits: ["previous"],
  setup(__props, { emit }: { emit: ({
	(e: 'previous'): void;
}), expose: any, slots: any, attrs: any }) {



const orderStore = useOrderStore();
const { loading } = useGlobalLoader();

const captureContext = ref<string>(null);

const expFormat: FormatOptions = {
	emitFormattedValue: true,
	validateAgainstFormattedValue: true,
	type: 'cc-expiration',
};

const paymentChoice = ref<PrepaymentOption | null>(null);
const prepay = computed(() => paymentChoice.value == null
	? null
	: paymentChoice.value !== PrepaymentOption.None
);

const creditCardData: CreditCardDetails = reactive({
	ccExpDate: '',
	csNumber: null,
	ccSave: false,
	ccCardType: null,
	inlineValidationError: null,
	sameAsShipping: false,
	billingAddress: {
		firstName: '',
		lastName: '',
		company: '',
		address1: '',
		address2: '',
		zip: '',
		city: '',
		state: ''
	},
	makeDefault: false,
});

const loadingZip = ref(false);
const paymentError = computed(() => orderStore.globalError?.type === WorkOrderErrorType.PaymentError);

const canContinue = computed(() => !loading.value && !loadingZip.value && prepay.value != null);

// TODO: populate from session
const errors = reactive<any>({});

const payments = computed(() => orderStore.session?.paymentInfo?.paymentMethods ?? []);
const showCreditCardForm = computed(() =>
	!!captureContext.value
	&& orderStore.session.paymentInfo.hasRemainingBalance
);

const { paymentService, logService } = useServices();

const applyGiftCardModal = reactive(useApplyGiftCardModal(true));

const orderedCheckoutPayments = computed(() => [...payments.value].sort((a, b) => a.amount < b.amount ? -1 : 1));
const paymentToRemove = ref<ICheckoutPayment | null>(null);
const removingPaymentMethod = ref(false);
const autoSchedulerPaymentForm = ref<HTMLElement | null>(null);
const automotiveBForm = ref<InstanceType<typeof BForm> | null>(null);

function confirmRemovePayment(payment: ICheckoutPayment) {
	paymentToRemove.value = payment;
}

async function removePaymentMethod() {
	try {
		removingPaymentMethod.value = true;
		const session = await paymentService.removePaymentMethod(paymentToRemove.value.paymentMethodID);
		orderStore.setSession(session);
	} catch (e) {
		logService.log(e);
		orderStore.setGlobalErrorMessage(DEFAULT_GLOBAL_ERROR_MESSAGE);
	}

	paymentToRemove.value = null;
	removingPaymentMethod.value = false;
}

async function handleSubmit() {
	if (loading.value) {
		return;
	}

	if (prepay.value && orderStore.session.paymentInfo.hasRemainingBalance) {
		const microform = tryGetMicroformFromBForm(automotiveBForm.value);
		const result = await orderStore.tryGetTransientToken(creditCardData, microform, captureContext.value);
		if (!result.success) {
			if (result.globalErrorMessage) {
				setPaymentPageError(result.globalErrorMessage);
			} else if (creditCardData.inlineValidationError) {
				//Works as long as microform is first form field; focusNextError will not find this field
				const firstFocusable = getIntialFocusElement(autoSchedulerPaymentForm.value);
				if (firstFocusable) {
					firstFocusable.focus();
				}
			}
			return;
		}
	}

	await orderStore.submitPayment(prepay.value);
}

function setPaymentPageError(message: string) {
	orderStore.setGlobalErrorMessage(message, true, WorkOrderErrorType.PaymentError);
	nextTick(() => focusNextError());
}

const paymentFlow = computed(() => orderStore.session.paymentInfo?.paymentFlow);

const possibleTotals = computed(() => orderStore.session.paymentInfo?.possibleTotals ?? []);
const inFullOption = computed(() => possibleTotals.value[PrepaymentOption.InFull]);

const payInFullWorkOrderDisclaimerId = computed(() => !!inFullOption.value && orderStore.session?.workOrderSummary?.totalIsNotFinal
	? WORK_ORDER_DISCLAIMER_ID
	: undefined);

watch(paymentFlow, flow => {
	if (flow == null) {
		return;
	}

	const matchingOption = possibleTotals.value[paymentChoice.value];

	if (!matchingOption) {
		paymentChoice.value = deriveCurrentPaymentType(orderStore.session.paymentInfo);
	}
}, {
	immediate: true
});


/**
 * Derives the currently selected OR required payment choice from backend data. Returns null if it cannot be determined.
 */
function deriveCurrentPaymentType(paymentInfo: IWorkOrderPaymentInfoResource): PrepaymentOption | null {
	if (!paymentInfo) {
		return null;
	}

	const { paymentFlow, prePay } = paymentInfo;


	switch (paymentFlow) {
		case WorkOrderPaymentFlow.PrePayUnavailable:
			return PrepaymentOption.None;
		case WorkOrderPaymentFlow.PrePayOptional:
			if (prePay) {
				return PrepaymentOption.InFull;
			}
			break;
		default:
			return null;
	}

	return null;
}

async function setUpMicroforms(): Promise<void> {
	try {
		const result = await orderStore.getCSMicroformContext();
		await loadFlexMicroformsClientLibrary(result);
		captureContext.value = result.context;
		if (!captureContext.value) {
			setPaymentPageError(cyberSourceErrors.CONNECTION_ERROR_AUTO);
			bugsnagClient.notify('Failed to generate capure context for CyberSource flex microform');
			return;
		}
	} catch (e) {
		setPaymentPageError(cyberSourceErrors.CONNECTION_ERROR_AUTO);
		bugsnagClient.notify('Failed to generate CyberSource flex microform');
	}
}

function updateCreditCardInlineValidation(errorMessage: string) {
	creditCardData.inlineValidationError = errorMessage;
}

function updateCreditCardType(type: string) {
	creditCardData.ccCardType = type;
}

onBeforeMount(() => {
	paymentChoice.value = deriveCurrentPaymentType(orderStore.session?.paymentInfo);
});

onMounted(async () => {
	await setUpMicroforms();

	if (paymentError.value) {
		nextTick(() => focusNextError());
	}
});

return { __sfc: true,emit, orderStore, loading, captureContext, expFormat, paymentChoice, prepay, creditCardData, loadingZip, paymentError, canContinue, errors, payments, showCreditCardForm, paymentService, logService, applyGiftCardModal, orderedCheckoutPayments, paymentToRemove, removingPaymentMethod, autoSchedulerPaymentForm, automotiveBForm, confirmRemovePayment, removePaymentMethod, handleSubmit, setPaymentPageError, paymentFlow, possibleTotals, inFullOption, payInFullWorkOrderDisclaimerId, deriveCurrentPaymentType, setUpMicroforms, updateCreditCardInlineValidation, updateCreditCardType, validation, ApplyGiftCardModal, BForm, BRadioGroup, BTextField, ConfirmRemovalModal, CreditCardIcons, CreditCardNumberField, FieldNote, Icon, LinkButton, PaymentStepOption, SelectedPayment, StepControls, TransitionExpand, ZipAddressInput, AutomotiveSchedulerStep, PrepaymentOption, WorkOrderPaymentFlow, cyberSourceErrors, DEFAULT_GLOBAL_ERROR_MESSAGE }
}

})